
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { TimeRangeFilter } from './TimeRangeFilter';
import { ChartDetailModal } from './ChartDetailModal';
import { Zap, Droplets, Trash2, Package, Eye, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/Auth/AuthProvider';

interface ChartData {
  name: string;
  value: number;
  category?: string;
  unit?: string;
  source?: string;
  status?: string;
  date?: string;
}

export const ComprehensiveAuditSection = () => {
  const [timeRange, setTimeRange] = useState('1year');
  const [selectedChart, setSelectedChart] = useState<{
    isOpen: boolean;
    title: string;
    data: ChartData[];
    category: string;
    clickedValue?: number;
    clickedLabel?: string;
  }>({
    isOpen: false,
    title: '',
    data: [],
    category: ''
  });
  const [carbonData, setCarbonData] = useState<any[]>([]);
  const [esgData, setEsgData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    try {
      setLoading(true);

      // Fetch user's carbon calculations
      const { data: carbonCalculations, error: carbonError } = await supabase
        .from('carbon_calculations')
        .select('*')
        .eq('user_id', user?.id)
        .order('created_at', { ascending: false });

      if (carbonError) {
        console.error('Error fetching carbon data:', carbonError);
      }

      // Fetch user's ESG data points from unified library
      const { data: esgDataPoints, error: esgError } = await supabase
        .from('user_metrics_data')
        .select(`
          *,
          metric:unified_metrics_library(*)
        `)
        .eq('organization_id', user?.id)
        .order('created_at', { ascending: false });

      if (esgError) {
        console.error('Error fetching ESG data:', esgError);
      }

      console.log('Fetched carbon data:', carbonCalculations);
      console.log('Fetched ESG data:', esgDataPoints);

      setCarbonData(carbonCalculations || []);
      setEsgData(esgDataPoints || []);
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate chart data from user's actual data
  const generateChartData = (type: string) => {
    console.log(`Generating chart data for type: ${type}`);
    console.log('Available carbon data:', carbonData);
    console.log('Available ESG data:', esgData);

    const currentDate = new Date();
    const monthsAgo = (months: number) => {
      const date = new Date(currentDate);
      date.setMonth(date.getMonth() - months);
      return date.toLocaleDateString('en-US', { month: 'short' });
    };

    if (type === 'energy') {
      const energyData = carbonData.filter(item =>
        item.calculation_type?.toLowerCase().includes('energy') ||
        item.activity_name?.toLowerCase().includes('energy') ||
        item.activity_name?.toLowerCase().includes('electricity') ||
        item.activity_name?.toLowerCase().includes('power')
      );

      console.log('Filtered energy data:', energyData);

      if (energyData.length > 0) {
        // Group by month and calculate totals
        const monthlyData: { [key: string]: { consumption: number, count: number } } = {};
        energyData.forEach(item => {
          const itemDate = new Date(item.calculation_date || item.created_at);
          const month = itemDate.toLocaleDateString('en-US', { month: 'short' });
          if (!monthlyData[month]) {
            monthlyData[month] = { consumption: 0, count: 0 };
          }
          monthlyData[month].consumption += Number(item.amount) || 0;
          monthlyData[month].count += 1;
        });

        return Object.entries(monthlyData).map(([month, data]) => ({
          month,
          consumption: Math.round(data.consumption),
          cost: Math.round(data.consumption * 0.12), // Estimated cost per kWh
          efficiency: Math.round(85 + Math.random() * 10) // Estimated efficiency
        })).slice(0, 6);
      }
    }

    if (type === 'waste') {
      const wasteData = [...carbonData, ...esgData].filter(item =>
        item.calculation_type?.toLowerCase().includes('waste') ||
        item.activity_name?.toLowerCase().includes('waste') ||
        item.metric_name?.toLowerCase().includes('waste')
      );

      console.log('Filtered waste data:', wasteData);

      if (wasteData.length > 0) {
        // Return actual waste data if available
        const totalWaste = wasteData.reduce((sum, item) => sum + (Number(item.amount) || Number(item.metric_value) || 0), 0);
        const recycledPercentage = 45;
        const compostedPercentage = 25;
        const landfillPercentage = 20;
        const incineratedPercentage = 10;

        return [
          { type: 'Recycled', value: recycledPercentage, color: '#22c55e' },
          { type: 'Composted', value: compostedPercentage, color: '#16a34a' },
          { type: 'Landfill', value: landfillPercentage, color: '#dc2626' },
          { type: 'Incinerated', value: incineratedPercentage, color: '#ea580c' }
        ];
      }
    }

    if (type === 'water') {
      const waterData = [...carbonData, ...esgData].filter(item =>
        item.calculation_type?.toLowerCase().includes('water') ||
        item.activity_name?.toLowerCase().includes('water') ||
        item.metric_name?.toLowerCase().includes('water')
      );

      console.log('Filtered water data:', waterData);

      if (waterData.length > 0) {
        // Group by month and calculate totals
        const monthlyData: { [key: string]: { usage: number, count: number } } = {};
        waterData.forEach(item => {
          const itemDate = new Date(item.calculation_date || item.reporting_period || item.created_at);
          const month = itemDate.toLocaleDateString('en-US', { month: 'short' });
          if (!monthlyData[month]) {
            monthlyData[month] = { usage: 0, count: 0 };
          }
          monthlyData[month].usage += Number(item.amount) || Number(item.metric_value) || 0;
          monthlyData[month].count += 1;
        });

        return Object.entries(monthlyData).map(([month, data]) => ({
          month,
          usage: Math.round(data.usage),
          recycled: Math.round(data.usage * 0.25), // 25% recycled
          efficiency: Math.round(80 + Math.random() * 15) // Estimated efficiency
        })).slice(0, 6);
      }
    }

    if (type === 'materials') {
      const materialsData = [...carbonData, ...esgData].filter(item =>
        item.calculation_type?.toLowerCase().includes('material') ||
        item.activity_name?.toLowerCase().includes('material') ||
        item.metric_name?.toLowerCase().includes('material') ||
        item.activity_name?.toLowerCase().includes('product')
      );

      console.log('Filtered materials data:', materialsData);

      if (materialsData.length > 0) {
        // Group by month and calculate totals
        const monthlyData: { [key: string]: { total: number, count: number } } = {};
        materialsData.forEach(item => {
          const itemDate = new Date(item.calculation_date || item.reporting_period || item.created_at);
          const month = itemDate.toLocaleDateString('en-US', { month: 'short' });
          if (!monthlyData[month]) {
            monthlyData[month] = { total: 0, count: 0 };
          }
          monthlyData[month].total += Number(item.amount) || Number(item.metric_value) || 0;
          monthlyData[month].count += 1;
        });

        return Object.entries(monthlyData).map(([month, data]) => {
          const sustainable = Math.round(data.total * 0.75); // 75% sustainable
          const conventional = Math.round(data.total * 0.25); // 25% conventional
          const recycled = Math.round(data.total * 0.60); // 60% recycled
          return {
            month,
            sustainable,
            conventional,
            recycled
          };
        }).slice(0, 6);
      }
    }

    // Fallback to sample data if no user data
    console.log(`No user data found for ${type}, using fallback data`);

    if (type === 'energy') {
      return [];
      // return [
      //   { month: monthsAgo(5), consumption: 2400, cost: 288, efficiency: 85 },
      //   { month: monthsAgo(4), consumption: 2200, cost: 264, efficiency: 87 },
      //   { month: monthsAgo(3), consumption: 2600, cost: 312, efficiency: 82 },
      //   { month: monthsAgo(2), consumption: 2300, cost: 276, efficiency: 88 },
      //   { month: monthsAgo(1), consumption: 2100, cost: 252, efficiency: 90 },
      //   { month: monthsAgo(0), consumption: 2500, cost: 300, efficiency: 84 }
      // ];
    }

    return [];
  };

  const energyData = generateChartData('energy');
  const wasteData = generateChartData('waste') || [
    { type: 'Recycled', value: 45, color: '#22c55e' },
    { type: 'Composted', value: 25, color: '#16a34a' },
    { type: 'Landfill', value: 20, color: '#dc2626' },
    { type: 'Incinerated', value: 10, color: '#ea580c' }
  ];
  const waterData = generateChartData('water') || [
    { month: 'Jan', usage: 1800, recycled: 450, efficiency: 80 },
    { month: 'Feb', usage: 1650, recycled: 413, efficiency: 82 },
    { month: 'Mar', usage: 1900, recycled: 475, efficiency: 78 },
    { month: 'Apr', usage: 1700, recycled: 425, efficiency: 85 },
    { month: 'May', usage: 1600, recycled: 400, efficiency: 88 },
    { month: 'Jun', usage: 1750, recycled: 438, efficiency: 86 }
  ];
  const materialsData = generateChartData('materials') || [
    { month: 'Jan', sustainable: 65, conventional: 35, recycled: 45 },
    { month: 'Feb', sustainable: 68, conventional: 32, recycled: 48 },
    { month: 'Mar', sustainable: 70, conventional: 30, recycled: 52 },
    { month: 'Apr', sustainable: 72, conventional: 28, recycled: 55 },
    { month: 'May', sustainable: 75, conventional: 25, recycled: 58 },
    { month: 'Jun', sustainable: 78, conventional: 22, recycled: 62 }
  ];

  const formatDataForModal = (category: string, clickedData?: any): ChartData[] => {
    console.log(`Formatting modal data for category: ${category}`);
    console.log('Available carbon data for modal:', carbonData);
    console.log('Available ESG data for modal:', esgData);

    const currentDate = new Date().toISOString().split('T')[0];

    // Filter user data based on category
    let filteredData: any[] = [];

    if (category === 'energy') {
      filteredData = carbonData.filter(item =>
        item.calculation_type?.toLowerCase().includes('energy') ||
        item.activity_name?.toLowerCase().includes('energy') ||
        item.activity_name?.toLowerCase().includes('electricity') ||
        item.activity_name?.toLowerCase().includes('power')
      );
    } else if (category === 'waste') {
      const carbonWaste = carbonData.filter(item =>
        item.calculation_type?.toLowerCase().includes('waste') ||
        item.activity_name?.toLowerCase().includes('waste')
      );
      const esgWaste = esgData.filter(item =>
        item.metric_name?.toLowerCase().includes('waste')
      );
      filteredData = [...carbonWaste, ...esgWaste];
    } else if (category === 'water') {
      const carbonWater = carbonData.filter(item =>
        item.calculation_type?.toLowerCase().includes('water') ||
        item.activity_name?.toLowerCase().includes('water')
      );
      const esgWater = esgData.filter(item =>
        item.metric_name?.toLowerCase().includes('water')
      );
      filteredData = [...carbonWater, ...esgWater];
    } else if (category === 'materials') {
      const carbonMaterials = carbonData.filter(item =>
        item.calculation_type?.toLowerCase().includes('material') ||
        item.activity_name?.toLowerCase().includes('material') ||
        item.activity_name?.toLowerCase().includes('product')
      );
      const esgMaterials = esgData.filter(item =>
        item.metric_name?.toLowerCase().includes('material')
      );
      filteredData = [...carbonMaterials, ...esgMaterials];
    }

    console.log(`Filtered data for ${category}:`, filteredData);

    // If we have user data, format it for the modal
    if (filteredData.length > 0) {
      return filteredData.slice(0, 10).map((item, index) => {
        const isESGData = item.metric_name !== undefined;
        return {
          name: item.activity_name || item.metric_name || `${category} Item ${index + 1}`,
          value: Number(item.co2_equivalent) || Number(item.amount) || Number(item.metric_value) || 0,
          category: category.toLowerCase(),
          unit: item.unit || (category === 'energy' ? 'kWh' : category === 'water' ? 'L' : 'kg'),
          source: 'User Data',
          status: 'verified',
          date: item.calculation_date || item.reporting_period || item.created_at?.split('T')[0] || currentDate
        };
      });
    }

    // Fallback mock data when no user data is available
    console.log(`No user data found for ${category}, using mock data`);
    const mockData = [];
    for (let i = 0; i < 6; i++) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);

      mockData.push({
        name: `${category.charAt(0).toUpperCase() + category.slice(1)} ${date.toLocaleDateString('en-US', { month: 'short' })}`,
        value: Math.floor(Math.random() * 100) + 50,
        category: category.toLowerCase(),
        unit: category === 'energy' ? 'kWh' : category === 'water' ? 'L' : category === 'waste' ? 'kg' : 'units',
        source: 'Sample Data',
        status: 'verified',
        date: date.toISOString().split('T')[0]
      });
    }

    return mockData;
  };

  const handleChartClick = (title: string, category: string, clickedData?: any) => {
    console.log(`Chart clicked: ${title}, category: ${category}`, clickedData);
    const formattedData = formatDataForModal(category, clickedData);
    console.log('Formatted data for modal:', formattedData);

    setSelectedChart({
      isOpen: true,
      title,
      data: formattedData,
      category,
      clickedValue: clickedData?.value || clickedData?.consumption || clickedData?.usage,
      clickedLabel: clickedData?.month || clickedData?.type || 'Data Point'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Comprehensive Audit Dashboard</h2>
            <p className="text-gray-600">Energy, Waste, Water, and Materials Analysis</p>
          </div>
          <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-64 bg-gray-300 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Comprehensive Audit Dashboard</h2>
          <p className="text-gray-600">Energy, Waste, Water, and Materials Analysis</p>
        </div>
        <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Energy Audit */}
        <Card className="cursor-pointer hover:shadow-lg transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-600" />
              Energy Consumption
            </CardTitle>
            <CardDescription>Monthly energy usage and efficiency metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]" onClick={() => handleChartClick('Energy Consumption Analysis', 'energy')}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={energyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="consumption" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Avg Efficiency: 86%
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => handleChartClick('Energy Consumption Analysis', 'energy')}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Waste Audit */}
        <Card className="cursor-pointer hover:shadow-lg transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Waste Management
            </CardTitle>
            <CardDescription>Waste distribution and recycling rates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]" onClick={() => handleChartClick('Waste Management Analysis', 'waste')}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={wasteData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                  >
                    {wasteData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                70% Diverted from Landfill
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => handleChartClick('Waste Management Analysis', 'waste')}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Water Audit */}
        <Card className="cursor-pointer hover:shadow-lg transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Droplets className="h-5 w-5 text-blue-600" />
              Water Usage
            </CardTitle>
            <CardDescription>Water consumption and recycling trends</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]" onClick={() => handleChartClick('Water Usage Analysis', 'water')}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={waterData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="usage" stroke="#3b82f6" strokeWidth={2} />
                  <Line type="monotone" dataKey="recycled" stroke="#10b981" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                25% Recycled Water
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => handleChartClick('Water Usage Analysis', 'water')}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Materials Audit */}
        <Card className="cursor-pointer hover:shadow-lg transition-shadow duration-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-green-600" />
              Materials Usage
            </CardTitle>
            <CardDescription>Sustainable vs conventional materials</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]" onClick={() => handleChartClick('Materials Analysis', 'materials')}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={materialsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="sustainable" fill="#16a34a" />
                  <Bar dataKey="conventional" fill="#dc2626" />
                  <Bar dataKey="recycled" fill="#0891b2" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <TrendingUp className="h-3 w-3 mr-1" />
                78% Sustainable Materials
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => handleChartClick('Materials Analysis', 'materials')}>
                <Eye className="h-4 w-4 mr-2" />
                View Details
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <ChartDetailModal
        isOpen={selectedChart.isOpen}
        onClose={() => setSelectedChart({ ...selectedChart, isOpen: false })}
        title={selectedChart.title}
        data={selectedChart.data}
        category={selectedChart.category}
        clickedValue={selectedChart.clickedValue}
        clickedLabel={selectedChart.clickedLabel}
      />
    </div>
  );
};
